import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Moon, Sparkles, CheckCircle, Thermometer, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function BeautySleepGuide() {
  const navigate = useNavigate();

  const sleepBenefits = [
    { benefit: "Collagen Production", desc: "Peaks during deep sleep, keeping skin firm and plump" },
    { benefit: "Cell Turnover", desc: "Skin cell regeneration is 8x faster at night" },
    { benefit: "Blood Flow", desc: "Increased circulation delivers nutrients to skin cells" },
    { benefit: "Stress Hormone Drop", desc: "Cortisol decreases, reducing inflammation and breakouts" },
    { benefit: "Melatonin Release", desc: "This hormone is a powerful antioxidant for skin" },
    { benefit: "Growth Hormone", desc: "Repairs tissue and builds new cells while you sleep" }
  ];

  const optimizations = [
    { title: "Silk/Satin Pillowcase", why: "Reduces friction that causes wrinkles and hair breakage", priority: "High" },
    { title: "Temperature: 65-68°F", why: "Cool temps improve sleep quality and reduce puffiness", priority: "High" },
    { title: "Complete Darkness", why: "Light disrupts melatonin and skin repair cycles", priority: "High" },
    { title: "Elevate Head Slightly", why: "Reduces under-eye puffiness and fluid retention", priority: "Medium" },
    { title: "Humidifier", why: "Prevents skin dehydration, especially in winter", priority: "Medium" },
    { title: "Clean Sheets Weekly", why: "Removes bacteria, oils, and dead skin buildup", priority: "Medium" }
  ];

  const routineTiming = [
    { time: "By 8 PM", task: "Finish eating to aid digestion", icon: "🍽️" },
    { time: "By 9 PM", task: "Complete skincare routine", icon: "✨" },
    { time: "9-10 PM", task: "Wind-down activities (reading, stretching)", icon: "📖" },
    { time: "10 PM", task: "All screens off, in bed", icon: "📵" },
    { time: "10-10:30 PM", task: "Deep breathing or meditation", icon: "🧘" },
    { time: "6-7 AM", task: "Natural wake time (aim for 8 hours)", icon: "☀️" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-semibold mb-4">
            Week 3 • Day 7 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Beauty Sleep Optimization
          </h1>
          <p className="text-xl text-secondary-600">Sleep Your Way to Better Skin</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-indigo-500 to-blue-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Moon className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Tonight's Goal: 8 Hours of Quality Sleep</h3>
              <p className="text-indigo-100 text-lg">
                Sleep is your most powerful (and free!) beauty treatment. Poor sleep shows on your face within 24 hours. 
                Let's optimize your sleep environment for maximum skin repair.
              </p>
            </div>
          </div>
        </section>

        {/* Sleep Benefits */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">What Happens While You Sleep</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {sleepBenefits.map((item, index) => (
              <div key={index} className="p-4 bg-indigo-50 rounded-xl">
                <h3 className="font-bold text-indigo-700 mb-1">{item.benefit}</h3>
                <p className="text-sm text-secondary-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Optimizations */}
        <section className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl p-8 border-2 border-indigo-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Optimize Your Sleep Environment</h2>
          <div className="space-y-3">
            {optimizations.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-4 flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                <div className="flex items-center gap-3 md:w-1/3">
                  <CheckCircle className="w-5 h-5 text-indigo-500 flex-shrink-0" />
                  <h3 className="font-bold text-secondary-900">{item.title}</h3>
                </div>
                <p className="text-sm text-secondary-600 md:flex-1">{item.why}</p>
                <span className={`text-xs px-2 py-1 rounded-full w-fit ${
                  item.priority === 'High' ? 'bg-indigo-500 text-white' : 'bg-indigo-100 text-indigo-700'
                }`}>
                  {item.priority} Priority
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* Evening Timeline */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-2">
            <Clock className="w-6 h-6 text-indigo-500" />
            Ideal Evening Timeline
          </h2>
          <div className="space-y-3">
            {routineTiming.map((item, index) => (
              <div key={index} className="flex items-center gap-4 p-3 bg-indigo-50 rounded-lg">
                <span className="text-2xl">{item.icon}</span>
                <span className="font-bold text-indigo-700 w-24">{item.time}</span>
                <span className="text-secondary-700">{item.task}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Tonight's Checklist */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-indigo-500" />
            Tonight's Beauty Sleep Checklist
          </h2>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              "Change to silk/satin pillowcase",
              "Set bedroom to 65-68°F",
              "Close all blinds/curtains",
              "Complete skincare by 9 PM",
              "No screens after 10 PM",
              "8 hours of sleep (in bed by 10 PM)"
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 p-3 bg-indigo-50 rounded-lg text-secondary-700">
                <div className="w-5 h-5 border-2 border-indigo-300 rounded"></div>
                {item}
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default BeautySleepGuide;
